import { DealScreenRow } from 'app/models/deal-screen-row'
import { createReducer } from 'app/state/utils'
import {
  acceptedIds,
  invalidate,
  oneId,
  outdated,
  withRows,
  withStatus,
  withTermination,
} from 'app/state/utils/generation'
import * as dealTypes from 'app/state/ducks/deals/types'
import * as entityTypes from 'app/state/ducks/entities/types'
import * as amendmentTypes from 'app/state/ducks/deal-amendments/types'
import * as types from './types'

export interface DealScreenState {
  rows: DealScreenRow[]
  isLoading: boolean
  /**
   * True once a write has happened that the rows cannot be patched for, or a
   * read came back from before a write. The thunk reads once more for the
   * second; the grid reloads once, while on screen, for the first.
   */
  stale: boolean
  /**
   * Bumped by every write the store learns of. A request records the
   * generation it started under; a response from an older generation is
   * thrown away, because it predates a write the rows already reflect
   * (utils/generation.ts).
   */
  generation: number
  /**
   * Entity id → name, from the entity list as it passes through the store (the
   * deal form loads it when it opens). A saved deal's parties are named from
   * it, so a borrower or admin on no other row still gets its avatar without a
   * reload. Not a write: it never moves the generation.
   */
  entityNames: Record<string, string>
}

const initialState: DealScreenState = {
  rows: [],
  isLoading: false,
  stale: false,
  generation: 0,
  entityNames: {},
}

type Row = DealScreenRow

/**
 * The names the statement resolved server-side, read back off the rows, and
 * the entity list's names on top (fresher: the form that saved the deal loaded
 * it on open), so a saved deal is shown with every party's name without
 * another request.
 */
const namesOnGrid = (rows: Row[]) => {
  const names = new Map<string, string>()
  const take = (keys: { id: string }[], labels: string[]) =>
    keys.forEach((key, i) => {
      if (key.id && labels[i]) names.set(key.id, labels[i])
    })
  for (const row of rows) {
    take(row.borrowers, row.borrowerNames)
    take(row.adminEntity, row.adminEntityName)
    if (row.approved) {
      take(row.approved.borrowers, row.approved.borrowerNames)
      take(row.approved.adminEntity, row.approved.adminEntityName)
    }
  }
  return names
}

const keysOf = (list: unknown): { id: string }[] =>
  (Array.isArray(list) ? list : [])
    .filter(key => key?.id)
    .map(key => ({ id: key.id as string }))

/**
 * The fields of a saved record (the whole deal the create / edit responses
 * carry, or an amendment record flattened onto its deal) that the grid renders.
 */
const partyNames = (state: DealScreenState) => {
  const names = namesOnGrid(state.rows)
  for (const [id, name] of Object.entries(state.entityNames)) names.set(id, name)
  return names
}

const fieldsOf = (saved: any, state: DealScreenState, row?: Row) => {
  const names = partyNames(state)
  // The edit thunks dispatch the form's REQUEST body, which carries only what
  // the form edits: a field it lacks (the key admin, the utilized amount) is
  // kept from the row, never blanked — the bulk endpoints address a deal by
  // its key admin.
  const borrowers = keysOf(saved.borrowers ?? row?.borrowers)
  const adminEntity = keysOf(saved.adminEntity ?? row?.adminEntity)
  return {
    dealName: saved.dealName ?? row?.dealName ?? '',
    amount: saved.amount ?? row?.amount ?? null,
    utilizedAmount: saved.utilizedAmount ?? row?.utilizedAmount ?? '0',
    currency: saved.currency ?? row?.currency ?? null,
    startDate: saved.startDate ?? row?.startDate ?? null,
    maturity: saved.maturity ?? row?.maturity ?? null,
    accountManagementAdmin:
      saved.accountManagementAdmin ?? row?.accountManagementAdmin ?? null,
    borrowers,
    borrowerNames: borrowers.map(b => names.get(b.id) ?? ''),
    adminEntity,
    adminEntityName: adminEntity.map(a => names.get(a.id) ?? ''),
  }
}

/** The saved record in a create / edit action, if the response carried one. */
const savedRecord = (action: { payload?: any }) => {
  const saved = action.payload
  return saved?.id && Array.isArray(saved?.borrowers) ? saved : undefined
}

/** The values an amendment row carries of its approved deal. */
const approvedValuesOf = (row: Row): Row['approved'] => ({
  dealName: row.dealName,
  amount: row.amount,
  utilizedAmount: row.utilizedAmount,
  currency: row.currency,
  startDate: row.startDate,
  maturity: row.maturity,
  borrowers: row.borrowers,
  borrowerNames: row.borrowerNames,
  adminEntity: row.adminEntity,
  adminEntityName: row.adminEntityName,
})

/** A brand-new draft, as the statement would have returned it. */
const draftRow = (saved: any, state: DealScreenState): Row => ({
  id: saved.id,
  status: 'Draft',
  amendment: false,
  amendmentType: null,
  amendmentDate: null,
  systemId: '',
  holds: [],
  approved: null,
  terminationDate: null,
  ...fieldsOf(saved, state),
})

/**
 * The approved deal shows AS its amendment while one is in flight: the
 * amendment's values, `amendment: true`, its type, date and own id, and the
 * approved deal's own values kept under `approved`. Creating an amendment
 * therefore replaces the approved row; approving one makes its values the
 * deal's, deleting one brings the approved values back.
 */
const asAmendment = (row: Row, saved: any, state: DealScreenState): Row => ({
  ...row,
  ...fieldsOf(saved, state, row),
  amendment: true,
  status: 'Draft',
  transactionId: saved.transactionId ?? row.transactionId,
  amendmentType: saved.amendmentType ?? null,
  amendmentDate: saved.amendmentDate ?? null,
  approved: row.amendment ? row.approved : approvedValuesOf(row),
})

/** A termination created from the row menu: the answer carries the amendment's own id, not its record. */
const asTermination = (row: Row, amendmentId?: string): Row => ({
  ...row,
  amendment: true,
  status: 'Draft',
  transactionId: amendmentId,
  amendmentType: 'Termination',
  // the request's date is not in the answer; nothing on the grid shows it
  amendmentDate: null,
  approved: row.amendment ? row.approved : approvedValuesOf(row),
})

/** Approved: the amendment's values are the deal's now; a termination locks it. */
const asApprovedDeal = (row: Row, status: Row['status'] = 'Approved'): Row => ({
  ...row,
  amendment: false,
  status,
  transactionId: undefined,
  amendmentType: null,
  amendmentDate: null,
  approved: null,
})

/** Deleted: the approved deal's own values come back. */
const asRestoredDeal = (row: Row): Row => ({
  ...asApprovedDeal(row),
  ...(row.approved ?? {}),
})

/** Created or edited: the approved row shows as the amendment. */
const applyAmendmentRecord = (state: DealScreenState, action: any) => {
  const saved = savedRecord(action)
  if (!saved) return invalidate(state)
  return withRows(
    state,
    state.rows.map(row =>
      row.id === saved.id ? asAmendment(row, saved, state) : row,
    ),
  )
}

/**
 * The deal store's list reads (GET_DEALS): a bulk approve answers without the
 * system id the ledger assigned, and without the Signing Portal's holds, while
 * the page reloads that store after a bulk action anyway. Fill in what an
 * approved row still lacks from it — only blanks, never a status or a value,
 * so a list that is behind PQS cannot revert a write.
 */
const fillApprovedFromDealList = (state: DealScreenState, action: any) => {
  const approved = new Map<string, any>(
    (Array.isArray(action.payload) ? action.payload : [])
      .filter((deal: any) => deal?.id && deal?.status === 'Approved')
      .map((deal: any) => [deal.id, deal]),
  )
  if (approved.size === 0) return state
  let changed = false
  const rows = state.rows.map(row => {
    if (row.amendment || row.status !== 'Approved' || row.systemId) return row
    const deal = approved.get(row.id)
    if (!deal?.systemId) return row
    changed = true
    return {
      ...row,
      systemId: deal.systemId,
      holds: row.holds.length ? row.holds : (deal.holds ?? []),
    }
  })
  return changed ? { ...state, rows } : state
}

const isTermination = (row: Row) => row.amendmentType?.toLowerCase() === 'termination'

/**
 * The entity list (GET_ENTITIES) — loaded by the deal form when it opens, and
 * by any page that needs it. Its names are kept for the next saved deal, and
 * fill any avatar name a row is still missing; never a status or a value, and
 * not a write, so the generation does not move.
 */
const ingestEntityNames = (state: DealScreenState, action: any) => {
  const list = Array.isArray(action.payload) ? action.payload : []
  const listed = list.filter((e: any) => e?.id && e?.entityName)
  if (listed.length === 0) return state
  const entityNames = { ...state.entityNames }
  for (const e of listed) entityNames[e.id] = e.entityName
  const fill = (keys: { id: string }[], labels: string[]) =>
    keys.map((key, i) => labels[i] || entityNames[key.id] || '')
  const same = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i])
  let changed = false
  const rows = state.rows.map(row => {
    const borrowerNames = fill(row.borrowers, row.borrowerNames)
    const adminEntityName = fill(row.adminEntity, row.adminEntityName)
    const approved = row.approved
      ? {
          ...row.approved,
          borrowerNames: fill(row.approved.borrowers, row.approved.borrowerNames),
          adminEntityName: fill(row.approved.adminEntity, row.approved.adminEntityName),
        }
      : null
    const rowChanged =
      !same(borrowerNames, row.borrowerNames) ||
      !same(adminEntityName, row.adminEntityName) ||
      (approved !== null &&
        row.approved !== null &&
        (!same(approved.borrowerNames, row.approved.borrowerNames) ||
          !same(approved.adminEntityName, row.approved.adminEntityName)))
    if (!rowChanged) return row
    changed = true
    return { ...row, borrowerNames, adminEntityName, approved }
  })
  return { ...state, entityNames, rows: changed ? rows : state.rows }
}

const dealScreenReducer = createReducer(initialState)({
  [types.GET_DEAL_SCREEN]: (
    state: DealScreenState,
    action: { payload: { rows: Row[]; generation?: number } },
  ) => {
    // Fetched before a write the store has since applied: these rows would
    // undo it. Drop them and leave the store stale; the thunk reads once more.
    if (outdated(state, action.payload.generation)) return { ...state, stale: true }
    return { ...state, rows: action.payload.rows, stale: false }
  },
  [types.START_LOADING_DEAL_SCREEN]: (state: DealScreenState) => ({
    ...state,
    isLoading: true,
  }),
  [types.DEAL_SCREEN_LOAD_FAILED]: (state: DealScreenState) => ({
    ...state,
    stale: false,
  }),
  [types.STOP_LOADING_DEAL_SCREEN]: (state: DealScreenState) => ({
    ...state,
    isLoading: false,
  }),
  [types.INVALIDATE_DEAL_SCREEN]: invalidate,

  // The grid reads this store, but every deal command is dispatched through
  // the deals and deal-amendments ducks — by the row menu, by the details
  // page, by the Create New stepper. Rather than have every caller remember to
  // refresh the grid, the grid's own store listens for the same successes and
  // patches its rows — never refetches, because PQS projects asynchronously
  // and a read straight after the command can still return the old contracts.
  [dealTypes.SUBMIT_DEAL]: (state: DealScreenState, action: any) =>
    withRows(state, withStatus(oneId(action), 'Submitted', false)(state.rows)),
  [dealTypes.REJECT_DEAL]: (state: DealScreenState, action: any) =>
    withRows(state, withStatus(oneId(action), 'Draft', false)(state.rows)),
  // Approval assigns the systemId, and the approved record in the answer
  // carries it.
  [dealTypes.APPROVE_DEAL]: (state: DealScreenState, action: any) => {
    const [id] = oneId(action)
    if (!id) return state
    return withRows(
      state,
      state.rows.map(row =>
        !row.amendment && row.id === id
          ? { ...row, status: 'Approved', systemId: action.payload.systemId ?? row.systemId }
          : row,
      ),
    )
  },
  [types.SET_DEAL_SCREEN_STATUSES]: (
    state: DealScreenState,
    action: { payload: { ids: string[]; status: Row['status'] } },
  ) =>
    action.payload.ids.length
      ? withRows(
          state,
          withStatus(action.payload.ids, action.payload.status, false)(state.rows),
        )
      : state,
  // The delete answers per id; a row the ledger refused to delete stays.
  [dealTypes.DELETE_DEAL]: (state: DealScreenState, action: any) => {
    const gone = new Set(acceptedIds(action.payload))
    return gone.size
      ? withRows(
          state,
          state.rows.filter(row => row.amendment || !gone.has(row.id)),
        )
      : state
  },
  [dealTypes.ADD_DEALS]: (state: DealScreenState, action: any) => {
    const saved = savedRecord(action)
    if (!saved) return invalidate(state)
    return withRows(state, [draftRow(saved, state), ...state.rows])
  },
  [dealTypes.EDIT_DEALS]: (state: DealScreenState, action: any) => {
    const saved = savedRecord(action)
    if (!saved) return invalidate(state)
    return withRows(
      state,
      state.rows.map(row =>
        !row.amendment && row.id === saved.id
          ? { ...row, ...fieldsOf(saved, state, row), status: 'Draft' }
          : row,
      ),
    )
  },
  [dealTypes.GET_DEALS]: fillApprovedFromDealList,
  [entityTypes.GET_ENTITIES]: ingestEntityNames,

  // Amendments.
  [amendmentTypes.SUBMIT_DEAL_AMENDMENT]: (state: DealScreenState, action: any) =>
    withRows(state, withStatus(oneId(action), 'Submitted', true)(state.rows)),
  [amendmentTypes.REJECT_DEAL_AMENDMENT]: (state: DealScreenState, action: any) =>
    withRows(state, withStatus(oneId(action), 'Draft', true)(state.rows)),
  // Approved: the amendment's values are now the deal's. A termination moves
  // it to the Terminated grid (it is a locked deal now). When it was locked is
  // not in the answer: the Terminated grid shows it from the next read.
  [amendmentTypes.APPROVE_DEAL_AMENDMENT]: (state: DealScreenState, action: any) => {
    const [id] = oneId(action)
    if (!id) return state
    return withRows(
      state,
      state.rows.map(row =>
        row.amendment && row.id === id
          ? asApprovedDeal(row, isTermination(row) ? 'Terminated' : 'Approved')
          : row,
      ),
    )
  },
  // Deleted: the approved deal comes back with its own values, kept on the
  // row since the amendment appeared. The answer names the id the menu sent:
  // the amendment's own, or the deal's for a termination created in-session.
  [amendmentTypes.DELETE_DEAL_AMENDMENT]: (state: DealScreenState, action: any) => {
    const gone = new Set(acceptedIds(action.payload))
    if (!gone.size) return state
    return withRows(
      state,
      state.rows.map(row =>
        row.amendment && (gone.has(row.id) || (row.transactionId && gone.has(row.transactionId)))
          ? asRestoredDeal(row)
          : row,
      ),
    )
  },
  [amendmentTypes.ADD_DEAL_AMENDMENTS]: applyAmendmentRecord,
  // Terminated from the row menu (utils/generation.ts): the answer carries
  // ids, not a record, and asTermination builds the row from them.
  [amendmentTypes.DEAL_TERMINATED]: (state: DealScreenState, action: any) =>
    withTermination(state, action, asTermination),
  [amendmentTypes.EDIT_DEAL_AMENDMENTS]: applyAmendmentRecord,
})

export default dealScreenReducer
